// Carrom-specific sound effects + re-exports of the shared
// urgency-beep + sound-enabled toggle from the Ludo sounds module.
//
// Why a separate file:
//   - Carrom has three sounds Ludo doesn't (collision, pocket drop,
//     foul) so this file owns them.
//   - The urgency beep and the global sound on/off are shared
//     across the platform — re-exported from `../ludo/sounds` so
//     Carrom uses the exact same tone the user is already used to.
//
// All synthesis is Web Audio API. No asset files needed.

import {
  startUrgencyBeep as ludoStartUrgencyBeep,
  stopUrgencyBeep as ludoStopUrgencyBeep,
  setSoundEnabled as ludoSetSoundEnabled,
  playWinFanfare    as ludoPlayWinFanfare,
  playLossTone      as ludoPlayLossTone,
} from '../ludo/sounds';

let _ctx: AudioContext | null = null;
let _enabled = true;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!_ctx) {
    try {
      const Ctor = window.AudioContext
        || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      _ctx = new Ctor();
    } catch {
      return null;
    }
  }
  // Browsers create AudioContexts in 'suspended' state when the
  // page hasn't received a user gesture yet. Always try to resume:
  // if we're inside a gesture handler the resume succeeds, if not
  // the promise rejects silently and we try again next call.
  if (_ctx.state === 'suspended') {
    _ctx.resume().catch(() => {});
  }
  return _ctx;
}

/** Call this from a user-gesture handler (e.g. the bet-tile click,
 *  the first pointerdown on the striker) to unlock the AudioContext.
 *  Without this the per-shot collision/pocket sounds are silently
 *  dropped on browsers that gate Web Audio on user interaction. */
export function primeAudio() {
  getCtx();
}

let _unlockInstalled = false;
/** Defensive global-listener fallback — unlocks the ctx on the very
 *  first user input anywhere in the document. Same pattern Ludo
 *  uses, kept independent here so each game can manage its own
 *  context lifecycle. Idempotent. */
export function installAudioUnlock() {
  if (_unlockInstalled) return;
  if (typeof document === 'undefined') return;
  _unlockInstalled = true;
  const handler = () => {
    const c = getCtx();
    if (!c) return;
    // Play a silent blip to fully unlock on iOS Safari.
    try {
      const osc = c.createOscillator();
      const gain = c.createGain();
      gain.gain.setValueAtTime(0.0001, c.currentTime);
      osc.connect(gain).connect(c.destination);
      osc.start();
      osc.stop(c.currentTime + 0.02);
    } catch { /* ignore */ }
    document.removeEventListener('pointerdown', handler, true);
    document.removeEventListener('touchstart',  handler, true);
    document.removeEventListener('keydown',     handler, true);
  };
  document.addEventListener('pointerdown', handler, true);
  document.addEventListener('touchstart',  handler, true);
  document.addEventListener('keydown',     handler, true);
}

/** Master switch — pipes through to the Ludo module too so the
 *  urgency beep responds to the same toggle. */
export function setSoundEnabled(on: boolean) {
  _enabled = on;
  ludoSetSoundEnabled(on);
}

/** Coin-on-coin or striker-on-coin click — pure NOISE synthesis,
 *  no oscillators. Oscillators (any kind, square / sine / triangle /
 *  sawtooth) at any single frequency carry tonal information that
 *  the ear instantly identifies as electronic. Real percussive
 *  impacts are broadband noise events; the only way to NOT sound
 *  electronic is to never put a pure tone in the signal.
 *
 *  This version refines the noise-only approach:
 *    1. ONE noise buffer with `exp(-15·t)` decay baked in for a
 *       sub-millisecond attack — sharper than any gain ramp can
 *       achieve with the WebAudio scheduler granularity.
 *    2. TWO parallel bandpass filters fed from that buffer:
 *         fundamental ~4 kHz at high Q (resonant click)
 *         overtone   ~9 kHz at slightly lower Q (sparkle)
 *       Mixing both gives the noise a richer "small plastic disc"
 *       timbre than a single bandpass; the overtone is what
 *       distinguishes a coin click from generic hiss.
 *    3. NO oscillator anywhere in the signal path.
 *
 *  `intensity` (0–1) scales gain AND lowers the fundamental
 *  centre frequency (heavy hits sound bigger / lower than light
 *  grazes). Each call nudges the centre by ±200 Hz randomly so
 *  consecutive clicks vary subtly, the way two real coin clicks do. */
export function playCollision(intensity = 0.5) {
  if (!_enabled) return;
  const c = getCtx();
  if (!c) return;
  const t0 = c.currentTime;
  const i = Math.max(0, Math.min(1, intensity));

  // ── Noise buffer with steep baked-in decay ─────────────────
  const dur = 0.030;
  const sampleRate = c.sampleRate;
  const len = Math.max(1, Math.floor(sampleRate * dur));
  const buf = c.createBuffer(1, len, sampleRate);
  const data = buf.getChannelData(0);
  for (let n = 0; n < len; n++) {
    const t = n / len;
    // exp(-15·t) → 1.0 at start, 0.0067 at end. Sub-ms attack.
    const env = Math.exp(-15 * t);
    data[n] = (Math.random() * 2 - 1) * env;
  }
  const src = c.createBufferSource();
  src.buffer = buf;

  // ── Fundamental bandpass ────────────────────────────────────
  // 4.5 kHz at light grazes → 3.0 kHz at heavy hits. High Q (5)
  // makes the noise read as a resonant "tick" rather than hiss.
  const bp1 = c.createBiquadFilter();
  bp1.type = 'bandpass';
  bp1.frequency.value = 4500 - 1500 * i + (Math.random() - 0.5) * 400;
  bp1.Q.value = 5;
  const g1 = c.createGain();
  g1.gain.value = 1.0;

  // ── Overtone bandpass (~2.2× the fundamental) ───────────────
  // The overtone is what makes the click sound like plastic
  // rather than wood. Lower Q so it spreads more.
  const bp2 = c.createBiquadFilter();
  bp2.type = 'bandpass';
  bp2.frequency.value = bp1.frequency.value * 2.2;
  bp2.Q.value = 3;
  const g2 = c.createGain();
  g2.gain.value = 0.55;

  // ── Output stage: sum both bandpass paths, scale by intensity ─
  const out = c.createGain();
  out.gain.value = 0.13 + 0.45 * i;

  src.connect(bp1).connect(g1).connect(out);
  src.connect(bp2).connect(g2).connect(out);
  out.connect(c.destination);

  src.start(t0);
  src.stop(t0 + 0.05);
}

/** Pocket drop — a hollow "thud" when a coin falls in. Sine 180 Hz
 *  tailing to 60 Hz over 250 ms with a soft attack and decay. */
export function playPocketDrop() {
  if (!_enabled) return;
  const c = getCtx();
  if (!c) return;
  const t0 = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(180, t0);
  osc.frequency.exponentialRampToValueAtTime(60, t0 + 0.25);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(0.32, t0 + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.30);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + 0.32);
}

/** Foul buzzer — descending sawtooth 400→100 Hz over ~600 ms. Loud
 *  enough to be unmistakable when the striker pockets. */
export function playFoul() {
  if (!_enabled) return;
  const c = getCtx();
  if (!c) return;
  const t0 = c.currentTime;
  // Two-layer sound: a sawtooth descending body + a square chirp at start.
  const osc1 = c.createOscillator();
  const g1 = c.createGain();
  osc1.type = 'sawtooth';
  osc1.frequency.setValueAtTime(420, t0);
  osc1.frequency.exponentialRampToValueAtTime(110, t0 + 0.55);
  g1.gain.setValueAtTime(0.0001, t0);
  g1.gain.exponentialRampToValueAtTime(0.30, t0 + 0.02);
  g1.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.65);
  osc1.connect(g1).connect(c.destination);
  osc1.start(t0);
  osc1.stop(t0 + 0.7);

  const osc2 = c.createOscillator();
  const g2 = c.createGain();
  osc2.type = 'square';
  osc2.frequency.setValueAtTime(220, t0);
  g2.gain.setValueAtTime(0.0001, t0);
  g2.gain.exponentialRampToValueAtTime(0.15, t0 + 0.01);
  g2.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.20);
  osc2.connect(g2).connect(c.destination);
  osc2.start(t0);
  osc2.stop(t0 + 0.22);
}

// Queen-cover celebration — small triumphant arpeggio when the
// queen has been properly captured (queen sunk + cover coin sunk
// successfully). Four ascending notes (C5 -> E5 -> G5 -> C6) over
// ~ 600 ms with a 3x harmonic shimmer per note for bell timbre.
// Loud enough to mark a celebratory moment but not jarring next to
// the in-game collision sounds. Plays for BOTH players (capturer
// and opponent) since both sides go through applyShotGameState
// when the carrom_shot_resolved snapshot lands.
export function playQueenCovered() {
  if (!_enabled) return;
  const c = getCtx();
  if (!c) return;
  const t0 = c.currentTime;
  const notes = [
    { f:  523.25, at: 0.00 },  // C5
    { f:  659.25, at: 0.10 },  // E5
    { f:  783.99, at: 0.20 },  // G5
    { f: 1046.50, at: 0.32 },  // C6 (octave climax — the 'capture' moment)
  ];
  for (const n of notes) {
    // Sine fundamental — gentle bell body.
    const o1 = c.createOscillator();
    const g1 = c.createGain();
    o1.type = 'sine';
    o1.frequency.setValueAtTime(n.f, t0 + n.at);
    g1.gain.setValueAtTime(0.0001, t0 + n.at);
    g1.gain.exponentialRampToValueAtTime(0.20, t0 + n.at + 0.012);
    g1.gain.exponentialRampToValueAtTime(0.0001, t0 + n.at + 0.55);
    o1.connect(g1).connect(c.destination);
    o1.start(t0 + n.at);
    o1.stop(t0 + n.at + 0.60);

    // 3x harmonic for shimmer (bell-like).
    const o2 = c.createOscillator();
    const g2 = c.createGain();
    o2.type = 'sine';
    o2.frequency.setValueAtTime(n.f * 3, t0 + n.at);
    g2.gain.setValueAtTime(0.0001, t0 + n.at);
    g2.gain.exponentialRampToValueAtTime(0.05, t0 + n.at + 0.012);
    g2.gain.exponentialRampToValueAtTime(0.0001, t0 + n.at + 0.30);
    o2.connect(g2).connect(c.destination);
    o2.start(t0 + n.at);
    o2.stop(t0 + n.at + 0.32);
  }
}

// Re-export shared urgency beep so Carrom uses the same end-of-turn
// tone players are used to from Ludo.
export const startUrgencyBeep = ludoStartUrgencyBeep;
export const stopUrgencyBeep  = ludoStopUrgencyBeep;
// Match-result tones — same warm fanfare / minor-third loss tone
// every game uses, so winning / losing in Carrom feels consistent
// with Ludo. Wired into the result-screen reveal in Page.tsx.
export const playWinFanfare    = ludoPlayWinFanfare;
export const playLossTone      = ludoPlayLossTone;
// Your-turn alert — a CARROM-SPECIFIC two-note ascending bell
// (D5 -> A5, perfect fifth). Distinct from any in-shot sound
// (collision, pocket-drop, foul) so the player can never confuse
// it with something the opponent did. Total duration ~ 480 ms.
// Each note layers a sine fundamental + 2x harmonic for a soft
// bell timbre. Page.tsx schedules it with a 1.2 s delay after
// the shot snapshot so it lands AFTER the strike's collision
// sounds, reading clearly as "your move" rather than as part
// of the opponent's strike audio.
export function playYourTurnChime() {
  if (!_enabled) return;
  const c = getCtx();
  if (!c) return;
  const t0 = c.currentTime;
  const notes = [
    { f: 587.33, at: 0.00 },  // D5
    { f: 880.00, at: 0.18 },  // A5
  ];
  for (const n of notes) {
    const o1 = c.createOscillator();
    const g1 = c.createGain();
    o1.type = 'sine';
    o1.frequency.setValueAtTime(n.f, t0 + n.at);
    g1.gain.setValueAtTime(0.0001, t0 + n.at);
    g1.gain.exponentialRampToValueAtTime(0.22, t0 + n.at + 0.012);
    g1.gain.exponentialRampToValueAtTime(0.0001, t0 + n.at + 0.30);
    o1.connect(g1).connect(c.destination);
    o1.start(t0 + n.at);
    o1.stop(t0 + n.at + 0.32);

    const o2 = c.createOscillator();
    const g2 = c.createGain();
    o2.type = 'sine';
    o2.frequency.setValueAtTime(n.f * 2, t0 + n.at);
    g2.gain.setValueAtTime(0.0001, t0 + n.at);
    g2.gain.exponentialRampToValueAtTime(0.07, t0 + n.at + 0.012);
    g2.gain.exponentialRampToValueAtTime(0.0001, t0 + n.at + 0.20);
    o2.connect(g2).connect(c.destination);
    o2.start(t0 + n.at);
    o2.stop(t0 + n.at + 0.22);
  }
}

// Your-turn-AGAIN chime — a SHORTER, single-note version of the
// playYourTurnChime above. Plays when the player retains their turn
// after sinking a coin, so they have an audible signal that the
// board is theirs again. Single A5 (= the second note of the full
// chime) so the ear ties the two sounds together: full D5→A5
// sequence means "turn just came to you from the opponent"; just
// A5 alone means "you kept your turn". Total duration ~ 250 ms,
// quieter than the full chime so a string of sinks doesn't become
// noisy. Same sine + 2× harmonic timbre as playYourTurnChime so the
// two sounds feel like the same instrument.
export function playYourTurnAgainChime() {
  if (!_enabled) return;
  const c = getCtx();
  if (!c) return;
  const t0 = c.currentTime;
  const f = 880.00;  // A5

  // Fundamental — sine, slightly quieter than the full chime's note
  // so the retain feels "lighter" than the cross-turn alert.
  const o1 = c.createOscillator();
  const g1 = c.createGain();
  o1.type = 'sine';
  o1.frequency.setValueAtTime(f, t0);
  g1.gain.setValueAtTime(0.0001, t0);
  g1.gain.exponentialRampToValueAtTime(0.16, t0 + 0.010);
  g1.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25);
  o1.connect(g1).connect(c.destination);
  o1.start(t0);
  o1.stop(t0 + 0.27);

  // 2× harmonic shimmer to match the full chime's timbre.
  const o2 = c.createOscillator();
  const g2 = c.createGain();
  o2.type = 'sine';
  o2.frequency.setValueAtTime(f * 2, t0);
  g2.gain.setValueAtTime(0.0001, t0);
  g2.gain.exponentialRampToValueAtTime(0.05, t0 + 0.010);
  g2.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
  o2.connect(g2).connect(c.destination);
  o2.start(t0);
  o2.stop(t0 + 0.20);
}

// ─── UI tap — tactile click for bet tile / settings buttons ──────
// Short, low-volume square-wave click at 1200 Hz, ~50 ms decay.
// Mirrors `playTap()` in games/ludo/sounds.ts so every clickable
// surface in the carrom lobby feels just like Ludo's. Used on
// every onClick where the gesture should give immediate auditory
// feedback (bet tile, settings menu items, top-bar back/settings,
// quit-confirm choices, etc.).
export function playTap() {
  if (!_enabled) return;
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'square';
  const t0 = c.currentTime;
  osc.frequency.setValueAtTime(1200, t0);
  gain.gain.setValueAtTime(0.06, t0);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.05);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + 0.06);
}
